import { useCallback, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Upload,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Download,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useTerminology } from "@/contexts/TerminologyContext";
import {
  ALL_FIELDS,
  missingImportFields,
  applyMapping,
  autoMapHeaders,
  type ContactField,
  type ParsedRow,
} from "@/lib/contactsImportParse";

type Step = "upload" | "map" | "validate" | "preview" | "done";

type PreparedRow = {
  row_index: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  last_booking_date: string | null;
};

type ValidateResultRow = {
  row_index: number;
  ok: boolean;
  action: "create" | "update" | "skip" | "error";
  match_reason: "email" | "phone" | "email_and_phone" | null;
  changed_fields: string[];
  errors: string[];
  warnings: string[];
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  onImported: () => void;
};

const UNMAPPED = "__unmapped__";

export default function ContactsImportDialog({
  open,
  onOpenChange,
  businessId,
  onImported,
}: Props) {
  const { customer, customers } = useTerminology();
  const [step, setStep] = useState<Step>("upload");
  const [fileName, setFileName] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [mapping, setMapping] = useState<Record<ContactField, string | null>>(
    () =>
      ALL_FIELDS.reduce(
        (acc, f) => ({ ...acc, [f.key]: null }),
        {} as Record<ContactField, string | null>,
      ),
  );
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [validation, setValidation] = useState<{
    total: number;
    ok_count: number;
    error_count: number;
    create_count: number;
    update_count: number;
    results: ValidateResultRow[];
  } | null>(null);
  const [preparedPayload, setPreparedPayload] = useState<PreparedRow[]>([]);
  const [importSummary, setImportSummary] = useState<{
    inserted: number;
    updated: number;
    error_count: number;
    profile_error_count: number;
    insert_errors?: { message: string; details?: string }[];
  } | null>(null);

  const reset = useCallback(() => {
    setStep("upload");
    setFileName(null);
    setHeaders([]);
    setRows([]);
    setMapping(
      ALL_FIELDS.reduce(
        (acc, f) => ({ ...acc, [f.key]: null }),
        {} as Record<ContactField, string | null>,
      ),
    );
    setValidating(false);
    setCommitting(false);
    setParseError(null);
    setValidation(null);
    setPreparedPayload([]);
    setImportSummary(null);
  }, []);

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFile = async (file: File) => {
    setParseError(null);
    setFileName(file.name);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      let parsed: { headers: string[]; rows: ParsedRow[] };
      if (ext === "csv" || ext === "tsv" || ext === "txt") {
        parsed = await parseCsv(file);
      } else if (ext === "xlsx" || ext === "xls") {
        parsed = await parseXlsx(file);
      } else {
        throw new Error("Unsupported file type. Use .csv or .xlsx.");
      }
      if (parsed.rows.length === 0) throw new Error("Spreadsheet has no data rows.");
      if (parsed.rows.length > 5000) {
        throw new Error("Max 5000 rows per import. Split the file first.");
      }
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setMapping((prev) => ({ ...prev, ...autoMapHeaders(parsed.headers) }));
      setStep("map");
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
      setFileName(null);
    }
  };

  const missingRequired = missingImportFields(mapping);

  const runValidate = async () => {
    setValidating(true);
    setValidation(null);
    try {
      const payload = applyMapping(rows, mapping);
      const { data, error } = await supabase.functions.invoke("contacts-import", {
        body: {
          mode: "validate",
          business_id: businessId,
          rows: payload,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setPreparedPayload(payload);
      setValidation(data);
      setStep("validate");
    } catch (e) {
      toast.error(`Validation failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setValidating(false);
    }
  };

  const runCommit = async () => {
    setCommitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("contacts-import", {
        body: {
          mode: "commit",
          business_id: businessId,
          rows: preparedPayload,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const insertErrors = (data.insert_errors ?? []) as {
        message: string;
        details?: string;
      }[];
      setImportSummary({
        inserted: data.inserted ?? 0,
        updated: data.updated ?? 0,
        error_count: data.error_count ?? 0,
        profile_error_count: data.profile_error_count ?? 0,
        insert_errors: insertErrors,
      });
      setStep("done");
      onImported();
      const importedCount = (data.inserted ?? 0) + (data.updated ?? 0);
      if (importedCount > 0) {
        if ((data.profile_error_count ?? 0) > 0) {
          toast.warning(
            `Imported ${importedCount} ${importedCount === 1 ? customer.toLowerCase() : customers.toLowerCase()}, but ${data.profile_error_count} AI profile${data.profile_error_count === 1 ? "" : "s"} need a retry`,
          );
        } else {
          toast.success(
            `Imported ${importedCount} ${importedCount === 1 ? customer.toLowerCase() : customers.toLowerCase()} (${data.inserted ?? 0} created, ${data.updated ?? 0} updated)`,
          );
        }
      } else if (insertErrors.length > 0) {
        toast.error(`Import failed: ${insertErrors[0].message}`);
      } else {
        toast.error(`Imported 0 ${customers.toLowerCase()} — no rows passed`);
      }
    } catch (e) {
      toast.error(`Import failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setCommitting(false);
    }
  };

  const downloadErrorRows = () => {
    if (!validation) return;
    const errorRowIndices = new Set(
      validation.results.filter((r) => !r.ok).map((r) => r.row_index),
    );
    const errorMap = new Map(validation.results.map((r) => [r.row_index, r]));
    const headersOut = [...headers, "_dibs_errors"];
    const lines = [headersOut.map(csvEscape).join(",")];
    rows.forEach((row, i) => {
      const rowIndex = i + 2;
      if (!errorRowIndices.has(rowIndex)) return;
      const errs = errorMap.get(rowIndex)?.errors.join(" | ") ?? "";
      const cells = headers.map((h) => csvEscape(String(row[h] ?? "")));
      cells.push(csvEscape(errs));
      lines.push(cells.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contacts-import-errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const previewRows = useMemo(() => rows.slice(0, 5), [rows]);

  const validRows = useMemo(() => {
    if (!validation) return [] as PreparedRow[];
    const okIndices = new Set(
      validation.results.filter((r) => r.ok).map((r) => r.row_index),
    );
    return preparedPayload.filter((p) => okIndices.has(p.row_index));
  }, [validation, preparedPayload]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Import {customers.toLowerCase()}</DialogTitle>
          <DialogDescription>
            Bring your existing {customer.toLowerCase()} list into Dibs. Imported {customers.toLowerCase()} won't
            be messaged automatically.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <label
              htmlFor="contacts-import-file"
              className="flex flex-col items-center justify-center gap-2 border border-dashed border-input bg-card hover:bg-secondary rounded-lg p-10 cursor-pointer transition-colors"
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">
                {fileName ?? "Click to upload or drag and drop"}
              </span>
              <span className="text-xs text-muted-foreground">
                CSV or XLSX, up to 5000 rows
              </span>
              <input
                id="contacts-import-file"
                type="file"
                accept=".csv,.tsv,.txt,.xlsx,.xls"
                className="sr-only"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
            </label>
            {parseError && (
              <p className="text-sm text-destructive flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                {parseError}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              First time?{" "}
              <a
                href="/dibs-contacts-import-template.csv"
                download
                className="text-primary hover:underline font-medium"
              >
                Download the template
              </a>{" "}
              to skip the mapping step.
            </p>
          </div>
        )}

        {step === "map" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileSpreadsheet className="h-4 w-4" />
              <span>
                {fileName} — {rows.length} row{rows.length === 1 ? "" : "s"}
              </span>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-foreground">
                Column mapping
              </h3>
              <p className="text-sm text-muted-foreground -mt-2">
                Match each Dibs field to the right column in your file.
              </p>
              <div className="grid gap-3">
                {ALL_FIELDS.map((f) => (
                  <div key={f.key} className="grid grid-cols-2 gap-3 items-center">
                    <Label className="text-sm font-medium">
                      {f.label}
                      {f.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Select
                      value={mapping[f.key] ?? UNMAPPED}
                      onValueChange={(v) =>
                        setMapping((m) => ({
                          ...m,
                          [f.key]: v === UNMAPPED ? null : v,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Not mapped" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNMAPPED}>Not mapped</SelectItem>
                        {headers.map((h) => (
                          <SelectItem key={h} value={h}>
                            {h}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            {previewRows.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-bold uppercase tracking-wider text-foreground">
                  Preview
                </h3>
                <div className="border border-border/50 rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {headers.map((h) => (
                          <TableHead key={h} className="whitespace-nowrap">
                            {h}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewRows.map((r, i) => (
                        <TableRow key={i}>
                          {headers.map((h) => (
                            <TableCell key={h} className="whitespace-nowrap text-xs">
                              {String(r[h] ?? "")}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              Existing {customers.toLowerCase()} with the same normalized email or phone will be updated automatically. Identity conflicts will be held for review.
            </p>

            {missingRequired.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Map the required fields ({missingRequired.join(", ")}) to continue.
              </p>
            )}
          </div>
        )}

        {step === "validate" && validation && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <SummaryCard label="Total rows" value={validation.total} />
              <SummaryCard label="Ready to import" value={validation.ok_count} tone="ok" />
              <SummaryCard
                label="With errors"
                value={validation.error_count}
                tone={validation.error_count > 0 ? "error" : undefined}
              />
            </div>

            {validation.error_count > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-foreground">
                    Rows with errors
                  </h3>
                  <Button type="button" variant="outline" size="sm" onClick={downloadErrorRows}>
                    <Download className="h-4 w-4 mr-1" />
                    Download errors
                  </Button>
                </div>
                <div className="border border-border/50 rounded-md max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Row</TableHead>
                        <TableHead>Errors</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {validation.results
                        .filter((r) => !r.ok)
                        .slice(0, 50)
                        .map((r) => (
                          <TableRow key={r.row_index}>
                            <TableCell className="text-xs">{r.row_index}</TableCell>
                            <TableCell className="text-xs">{r.errors.join(", ")}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {validation.ok_count === 0 && (
              <p className="text-sm text-destructive">
                Nothing to import. Fix the errors and try again.
              </p>
            )}
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4">
            <div className="space-y-1">
              <h3 className="text-sm font-bold uppercase tracking-wider text-foreground">
                Preview before import
              </h3>
              <p className="text-sm text-muted-foreground">
                {validation?.create_count ?? validation?.ok_count ?? 0} {(validation?.create_count ?? validation?.ok_count) === 1 ? customer.toLowerCase() : customers.toLowerCase()} will be added and{" "}
                {validation?.update_count ?? 0} will be updated. Review and confirm.
              </p>
            </div>
            <div className="border border-border/50 rounded-md overflow-x-auto max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Last booking</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {validRows.map((r) => {
                    const decision = validation?.results.find((result) => result.row_index === r.row_index);
                    return (
                      <TableRow key={r.row_index}>
                        <TableCell className="text-sm font-medium">{r.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {decision?.action === "update"
                            ? `Update · ${decision.match_reason === "email_and_phone" ? "email + phone" : decision.match_reason ?? "existing"}`
                            : "Create"}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{r.email}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{r.phone || "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{r.last_booking_date || "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">
                          {r.notes || "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {step === "done" && importSummary && (
          <div className="space-y-4 py-4">
            <div className="text-center space-y-2">
              {importSummary.inserted + importSummary.updated > 0 ? (
                <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
              ) : (
                <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
              )}
              <h3 className="text-lg font-heading font-bold">
                Imported {importSummary.inserted + importSummary.updated} {importSummary.inserted + importSummary.updated === 1 ? customer.toLowerCase() : customers.toLowerCase()}
              </h3>
              {importSummary.inserted + importSummary.updated > 0 && (
                <p className="text-sm text-muted-foreground">
                  {importSummary.inserted} created · {importSummary.updated} updated
                </p>
              )}
              {importSummary.error_count > 0 && (
                <p className="text-sm text-muted-foreground">
                  {importSummary.error_count} row
                  {importSummary.error_count === 1 ? "" : "s"} skipped.
                </p>
              )}
              {importSummary.profile_error_count > 0 && (
                <p className="text-sm text-amber-700">
                  {importSummary.profile_error_count} AI profile
                  {importSummary.profile_error_count === 1 ? "" : "s"} could not be built yet. The {customer.toLowerCase()} data is safe; contact support to retry profile enrichment.
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === "upload" && (
            <Button type="button" variant="outline" onClick={() => handleClose(false)}>
              Cancel
            </Button>
          )}

          {step === "map" && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setStep("upload");
                  setRows([]);
                  setHeaders([]);
                  setFileName(null);
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button type="button" onClick={runValidate} disabled={missingRequired.length > 0 || validating}>
                {validating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ArrowRight className="h-4 w-4 mr-1" />}
                Validate
              </Button>
            </>
          )}

          {step === "validate" && (
            <>
              <Button type="button" variant="outline" onClick={() => setStep("map")}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button type="button" onClick={() => setStep("preview")} disabled={validation?.ok_count === 0}>
                <ArrowRight className="h-4 w-4 mr-1" />
                Preview {validation?.ok_count ?? 0} {validation?.ok_count === 1 ? customer.toLowerCase() : customers.toLowerCase()}
              </Button>
            </>
          )}

          {step === "preview" && (
            <>
              <Button type="button" variant="outline" onClick={() => setStep("validate")} disabled={committing}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button type="button" onClick={runCommit} disabled={committing || validRows.length === 0}>
                {committing && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Confirm and import {validRows.length} {validRows.length === 1 ? customer.toLowerCase() : customers.toLowerCase()}
              </Button>
            </>
          )}

          {step === "done" && (
            <Button type="button" onClick={() => handleClose(false)}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "ok" | "error";
}) {
  return (
    <div className="module-card text-center">
      <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">{label}</p>
      <p
        className={
          "text-2xl font-heading font-bold mt-1 " +
          (tone === "ok" ? "text-primary" : tone === "error" ? "text-destructive" : "text-foreground")
        }
      >
        {value}
      </p>
    </div>
  );
}

// File parsers (lazy-imported so the libs only load when this dialog opens)

async function parseCsv(file: File): Promise<{ headers: string[]; rows: ParsedRow[] }> {
  const Papa = (await import("papaparse")).default;
  return new Promise((resolve, reject) => {
    Papa.parse<ParsedRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        if (res.errors.length > 0 && res.data.length === 0) {
          reject(new Error(res.errors[0].message));
          return;
        }
        const headers = (res.meta.fields ?? []).filter((h) => h && h.trim());
        resolve({ headers, rows: res.data });
      },
      error: (err) => reject(err),
    });
  });
}

async function parseXlsx(file: File): Promise<{ headers: string[]; rows: ParsedRow[] }> {
  const XLSX = await import("xlsx");
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) throw new Error("No sheets found in the workbook.");
  const data = XLSX.utils.sheet_to_json<ParsedRow>(ws, { defval: "" });
  if (data.length === 0) return { headers: [], rows: [] };
  const headers = Object.keys(data[0]);
  return { headers, rows: data };
}

function csvEscape(v: string): string {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
